import React, { useEffect, useState } from 'react';
import { FormComponent, ModalComponent, UiComponent } from 'components';
import { SCHEDULE_TYPE_SELECT, ScheduleType } from 'types';
import { DraftType, TIME_REGEX, formatDayLabel, normalizeTime } from './utils';
import * as styles from './style';

type ErrorKey = 'date' | 'startTime' | 'endTime' | 'title';

interface Props {
  draft: DraftType;
  dates: string[];
  isSaving: boolean;
  onChange: (draft: DraftType) => void;
  onClose: () => void;
  onSubmit: (draft: DraftType) => void;
}

const validate = (draft: DraftType) => {
  const errors: Partial<Record<ErrorKey, string>> = {};
  if (!draft.date) errors.date = '날짜를 선택해 주세요.';
  if (!draft.startTime.trim()) errors.startTime = '시작 시간을 입력해 주세요.';
  else if (!TIME_REGEX.test(draft.startTime.trim()))
    errors.startTime = 'HH:MM 형식(24시간)으로 입력해 주세요.';
  const startTime = draft.startTime.trim();
  const endTime = draft.endTime.trim();
  if (endTime && !TIME_REGEX.test(endTime))
    errors.endTime = 'HH:MM 형식(24시간)으로 입력해 주세요.';
  else if (endTime && TIME_REGEX.test(startTime) && endTime < startTime)
    errors.endTime = '종료 시간은 시작 시간 이후로 입력해 주세요.';
  if (!draft.title.trim()) errors.title = '일정명을 입력해 주세요.';
  return errors;
};

function Drawer({
  draft,
  dates,
  isSaving,
  onChange,
  onClose,
  onSubmit,
}: Props) {
  const [errors, setErrors] = useState<Partial<Record<ErrorKey, string>>>({});

  useEffect(() => {
    setErrors({});
  }, [draft.id]);

  const set = <K extends keyof DraftType>(key: K, value: DraftType[K]) => {
    onChange({ ...draft, [key]: value });
    if (errors[key as ErrorKey]) setErrors({ ...errors, [key]: undefined });
  };

  const handleSubmit = () => {
    const normalized = {
      ...draft,
      startTime: normalizeTime(draft.startTime),
      endTime: normalizeTime(draft.endTime),
    };
    onChange(normalized);
    const nextErrors = validate(normalized);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    onSubmit(normalized);
  };

  return (
    <ModalComponent.Portal>
      <div css={styles.drawerBackdrop} />
      <aside css={styles.drawer}>
        <div css={styles.drawerHead}>
          {draft.id ? '일정 수정' : '일정 추가'}
        </div>
        <div css={styles.drawerBody}>
          <div css={styles.field}>
            <label>날짜 *</label>
            <FormComponent.Select
              options={dates.map((d, i) => ({
                name: formatDayLabel(d, i),
                value: d,
              }))}
              defaultTitle={dates.includes(draft.date) ? undefined : '선택'}
              value={dates.includes(draft.date) ? draft.date : ''}
              handleChange={(value) => set('date', value)}
            />
            {errors.date && <p css={styles.error}>{errors.date}</p>}
          </div>

          <div css={{ display: 'flex', gap: 12 }}>
            <div css={[styles.field, { flex: 1 }]}>
              <label>시작 시간 *</label>
              <FormComponent.Input
                placeholder="HH:MM"
                maxLength={5}
                value={draft.startTime}
                onChange={(e) => set('startTime', e.target.value)}
                onBlur={(e) => set('startTime', normalizeTime(e.target.value))}
              />
              {errors.startTime && <p css={styles.error}>{errors.startTime}</p>}
            </div>
            <div css={[styles.field, { flex: 1 }]}>
              <label>종료 시간</label>
              <FormComponent.Input
                placeholder="HH:MM"
                maxLength={5}
                value={draft.endTime}
                onChange={(e) => set('endTime', e.target.value)}
                onBlur={(e) => set('endTime', normalizeTime(e.target.value))}
              />
              {errors.endTime && <p css={styles.error}>{errors.endTime}</p>}
            </div>
          </div>
          <p css={[styles.note, { marginTop: -8, marginBottom: 16 }]}>
            종료 시간이 없는 집결 일정은 시작 시간만 입력합니다.
          </p>

          <div css={styles.field}>
            <label>일정명 *</label>
            <FormComponent.Input
              maxLength={100}
              value={draft.title}
              onChange={(e) => set('title', e.target.value)}
            />
            {errors.title && <p css={styles.error}>{errors.title}</p>}
          </div>

          <div css={styles.field}>
            <label>한 줄 정보</label>
            <FormComponent.Input
              maxLength={100}
              value={draft.summary}
              onChange={(e) => set('summary', e.target.value)}
            />
          </div>

          <div css={styles.field}>
            <label>유형</label>
            <FormComponent.Select<ScheduleType | ''>
              options={SCHEDULE_TYPE_SELECT}
              defaultTitle="선택 안 함"
              value={draft.type}
              handleChange={(value) => set('type', value)}
            />
          </div>

          <div css={styles.field}>
            <label>상세 설명</label>
            <FormComponent.Textarea
              rows={5}
              maxLength={2000}
              css={{ padding: '10px' }}
              value={draft.description}
              onChange={(e) => set('description', e.target.value)}
            />
          </div>

          <div css={styles.field}>
            <label>장소</label>
            <FormComponent.Input
              maxLength={255}
              value={draft.place}
              onChange={(e) => set('place', e.target.value)}
            />
          </div>

          <div css={styles.field}>
            <label>준비물 / 안내사항</label>
            <FormComponent.Textarea
              rows={4}
              maxLength={2000}
              css={{ padding: '10px' }}
              value={draft.notice}
              onChange={(e) => set('notice', e.target.value)}
            />
          </div>

          <p css={styles.note}>
            상세 정보가 있는 일정만 앱에서 눌렀을 때 상세 화면을 엽니다.
          </p>
        </div>
        <div css={styles.drawerFoot}>
          <UiComponent.Button variant="border" color="grey" onClick={onClose}>
            취소
          </UiComponent.Button>
          <UiComponent.Button
            variant="contained"
            color="black"
            disabled={isSaving}
            onClick={handleSubmit}
          >
            저장
          </UiComponent.Button>
        </div>
      </aside>
    </ModalComponent.Portal>
  );
}

export default Drawer;
